import { useState } from "react";
import { Link, Outlet, useLocation, useRouteLoaderData, useSearchParams, useSubmit } from "react-router-dom";

import ServerFooter from "../common/ServerFooter";
import UserInfo from "src/components/common/userInfo";
import { dateDurationCalc } from "src/components/common/functions";
import { useAuthStore, User } from "src/components/main/authStore";
import { PerDiem } from "./types";

export const extraFields = ["page", "limit", "createdAtFrom", "createdAtTo"];

export default function PerDiemList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useAuthStore(state => state.user) as User;
    const submit = useSubmit();
    const location = useLocation();
    const {
        perDiems,
        start: defaultStartDate,
        end: defaultEndDate,
        total,
    }: { perDiems: PerDiem[]; start: string; end: string; total: number } = useRouteLoaderData("PerDiemList")!;
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const onFilter = [...searchParams.keys()].filter(param => !extraFields.includes(param)).length > 0;

    const handlePaid = (perDiem: PerDiem) => {
        if (!window.confirm(`Are you sure to set this per diem ${perDiem.paid === "yes" ? "unpaid" : "paid"}?`)) return;
        const formData = new FormData();
        formData.append("id", String(perDiem.id));
        formData.append("paid", perDiem.paid === "yes" ? "no" : "yes");
        submit(formData, {
            method: "post",
            action: "Paid?" + searchParams.toString(),
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3">
                <span className = "me-2 fw-bold">Submission Date Range</span>
                <label htmlFor = "startDate" className = "text-nowrap me-2 mb-0">Start</label>
                <input
                    type = "date"
                    id = "startDate"
                    className = "form-control inline me-3"
                    value = {startDate}
                    max = {endDate}
                    onChange = {e => setStartDate(e.currentTarget.value)}
                />
                <label htmlFor = "endDate" className = "text-nowrap me-2 mb-0">End</label>
                <input
                    type = "date"
                    id = "endDate"
                    className = "form-control inline me-3"
                    value = {endDate}
                    min = {startDate}
                    onChange = {e => setEndDate(e.currentTarget.value)}
                />
                <button
                    type = "button"
                    className = "form-control inline me-5"
                    disabled = {defaultStartDate === startDate && defaultEndDate === endDate}
                    onClick = {() => {
                        const params = new URLSearchParams(searchParams);
                        params.set("createdAtFrom", startDate);
                        params.set("createdAtTo", endDate);
                        setSearchParams(params, { replace: false });
                    }}
                >
                    Load
                </button>
                <Link className = "btn btn-outline-primary pointer position-relative" to = {{ pathname: "Filter", search: location.search }} replace>
                    Filter
                    {onFilter ? <span className = "badge text-bg-primary position-absolute top-0 start-100 translate-middle" style = {{ minWidth: "2.2em" }}>on</span> : null}
                </Link>
                <Link className = "btn btn-outline-primary pointer ms-3" to = {{ pathname: "Export", search: location.search }}>
                    Export
                </Link>
                {user.perDiem !== "view" ? (
                    <Link className = "btn btn-outline-primary pointer ms-3" to = "Add">
                        Add
                    </Link>
                ) : null}
            </nav>
            {!perDiems.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Id</th>
                                <th>Requester</th>
                                <th>Department</th>
                                <th>Project</th>
                                <th>Request Dates</th>
                                <th>Duration</th>
                                <th>Hotel</th>
                                <th>Submitted On</th>
                                <th>Approver</th>
                                <th>Status</th>
                                <th>Paid</th>
                                {user.perDiem === "editAll" ? <th /> : null}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {perDiems.map(perDiem => (
                                <tr key = {perDiem.id}>
                                    <td>{perDiem.id}</td>
                                    <td>
                                        <UserInfo name = {perDiem.requesterName} id = {perDiem.requesterId} />
                                    </td>
                                    <td>{perDiem.requester.department}</td>
                                    <td>
                                        <Link to = {`/Projects/${perDiem.projectId}`}>{perDiem.projectName}</Link>
                                    </td>
                                    <td>{perDiem.startDate === perDiem.endDate ? perDiem.startDate : `${perDiem.startDate} ~ ${perDiem.endDate}`}</td>
                                    <td>{dateDurationCalc(perDiem.startDate, perDiem.endDate)}</td>
                                    <td>
                                        <div>{perDiem.hotelName}</div>
                                        <div>{perDiem.hotelAddress}</div>
                                    </td>
                                    <td>{perDiem.createdAt}</td>
                                    <td>
                                        <UserInfo name = {perDiem.approverName} id = {perDiem.approverId} />
                                    </td>
                                    <td>{perDiem.status}</td>
                                    <td>{perDiem.paid}</td>
                                    {user.perDiem === "editAll" ? (
                                        <td>
                                            <button
                                                type = "button"
                                                className = {perDiem.paid === "yes" ? "btn btn-info" : "btn btn-primary"}
                                                onClick = {() => handlePaid(perDiem)}
                                            >
                                                {perDiem.paid === "yes" ? "Unmark" : "Mark"}
                                            </button>
                                        </td>
                                    ) : null}
                                    <td>
                                        <Link className = "btn btn-outline-primary" to = {String(perDiem.id)}>
                                            Detail
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {total ? <ServerFooter length = {total} /> : null}
            <Outlet />
        </div>
    );
}
